/**
 * Reveals `.scroll-fade-in` elements (see base.css) with a slide-up fade-in
 * the first time they enter the viewport. Elements set their own
 * `--scroll-fade-in-delay` (ms) inline to stagger a group of siblings into
 * a "domino" cascade.
 *
 * No-ops entirely under prefers-reduced-motion, where the CSS never hides
 * the elements in the first place.
 */
import { onDocumentReady } from '@theme/utilities';

// `[data-scroll-fade-children] > *` covers containers (like the footer)
// whose items come from `content_for 'blocks'`, where there's no per-item
// liquid loop to hang a `.scroll-fade-in` class on directly.
const REVEAL_SELECTOR = '.scroll-fade-in, [data-scroll-fade-children] > *';
const VISIBLE_CLASS = 'scroll-fade-in--visible';

// Containers the theme hydrates/morphs after initial paint (see
// HYDRATION_KEY_ATTRIBUTE in assets/morph.js) - product recommendations'
// async fetch is the main one. Their skeleton/placeholder children don't
// start out matching REVEAL_SELECTOR, so childList watching alone can miss
// them turning into real `.scroll-fade-in` cards (see watchAsyncContainer).
const ASYNC_CONTAINER_SELECTOR = '[data-hydration-key]';

/** @type {IntersectionObserver | null} */
let intersectionObserver = null;

/** @type {MutationObserver | null} */
let domObserver = null;

// Elements that have already been revealed once, so a later class patch on
// them (see handleMutations) is known to be a morph stripping --visible
// rather than a first-time match that still needs the intersection check.
const revealed = new WeakSet();

function getIntersectionObserver() {
  intersectionObserver ??= new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
        obs.unobserve(entry.target);
      }
    },
    // No bottom rootMargin shrink here on purpose: a negative bottom margin
    // (to trigger the reveal slightly before an element fully scrolls into
    // view) excludes that margin's strip from the intersection area - which
    // an element sitting entirely within it, like the last footer bar flush
    // against the bottom of a maximally-scrolled short page, can never
    // satisfy (there's no more room to scroll it further up). Plain
    // viewport + a low threshold reveals just as soon in practice.
    { threshold: 0.1 }
  );

  return intersectionObserver;
}

function getDomObserver() {
  domObserver ??= new MutationObserver(handleMutations);
  return domObserver;
}

/** @param {MutationRecord[]} mutations */
function handleMutations(mutations) {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      const target = /** @type {Element} */ (mutation.target);

      if (revealed.has(target)) {
        // `assets/morph.js` (product recommendations' async fetch, theme-
        // editor re-renders, page-morph navigation) matches old/new DOM
        // nodes by tag name alone when there's no `id`, so it can patch a
        // preserved node's `class` attribute wholesale from freshly-
        // rendered server HTML that has no idea --visible was ever added.
        // Restore it before the browser paints, so an already-revealed
        // element never visibly resets.
        if (!target.classList.contains(VISIBLE_CLASS)) target.classList.add(VISIBLE_CLASS);
      } else if (target.matches(REVEAL_SELECTOR)) {
        // A watched async container's child just started matching (e.g. a
        // skeleton placeholder morphed in place into a real product card,
        // same node the whole time) - treat it like a first-time match and
        // run the normal intersection check rather than instantly revealing.
        observe(target);
      }
      continue;
    }

    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(REVEAL_SELECTOR)) observe(node);
      observeExisting(node);
      watchAsyncContainers(node);
    }
  }
}

/** @param {Element} element */
function reveal(element) {
  element.classList.add(VISIBLE_CLASS);
  revealed.add(element);

  // Scope the (comparatively expensive) attribute watch to just elements
  // that have actually been revealed, rather than the whole document.
  getDomObserver().observe(element, { attributes: true, attributeFilter: ['class'] });
}

/** @param {Element} element */
function observe(element) {
  if (element.classList.contains(VISIBLE_CLASS)) return;
  getIntersectionObserver().observe(element);
}

/** @param {ParentNode} root */
function observeExisting(root) {
  root.querySelectorAll(REVEAL_SELECTOR).forEach(observe);
}

/** @param {Element} container */
function watchAsyncContainer(container) {
  getDomObserver().observe(container, { attributes: true, attributeFilter: ['class'], subtree: true });
}

/** @param {ParentNode} root */
function watchAsyncContainers(root) {
  if (root instanceof Element && root.matches(ASYNC_CONTAINER_SELECTOR)) watchAsyncContainer(root);
  root.querySelectorAll(ASYNC_CONTAINER_SELECTOR).forEach(watchAsyncContainer);
}

onDocumentReady(() => {
  if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;

  observeExisting(document);
  watchAsyncContainers(document);

  // Sections can render `.scroll-fade-in` elements after the initial parse
  // (product recommendations fetched async, section morphs from the theme
  // editor, infinite-scroll pagination, cart/quick-add re-renders), so keep
  // watching for new matches rather than only scanning once at load.
  getDomObserver().observe(document.body, { childList: true, subtree: true });
});
